import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable, DataTableSelectionMultipleChangeEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { lagerCompaniesAPI, lagerFinishedProductsAPI, lagerTransportAPI } from '../services/api';
import { LagerCompany, LagerFinishedProduct, LagerTransport, LagerUnit } from '../types';
import { showCustomAlert } from './CustomAlert';

type TransportFormState = {
  productId: string;
  companyId: string;
  quantity: number;
  unit: LagerUnit;
  shippedDate: Date;
  squareMeters: number;
  shippedTo: string;
  notes: string;
};

const unitOptions: Array<{ label: string; value: LagerUnit }> = [
  { label: 'kom', value: 'kom' },
  { label: 'paleta', value: 'paleta' },
  { label: 'm²', value: 'm2' }
];

function toDate(value: string | Date | undefined): Date {
  if (!value) return new Date();
  const d = typeof value === 'string' ? new Date(value) : value;
  return isNaN(d.getTime()) ? new Date() : d;
}

const LagerTransportPage: React.FC = () => {
  const [transports, setTransports] = useState<LagerTransport[]>([]);
  const [selectedTransports, setSelectedTransports] = useState<LagerTransport[]>([]);
  const [products, setProducts] = useState<LagerFinishedProduct[]>([]);
  const [companies, setCompanies] = useState<LagerCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<LagerTransport | null>(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState<TransportFormState>({
    productId: '',
    companyId: '',
    quantity: 0,
    unit: 'paleta',
    shippedDate: new Date(),
    squareMeters: 0,
    shippedTo: '',
    notes: ''
  });

  const dialogHeader = useMemo(() => (editing ? 'Izmeni transport' : 'Dodaj transport'), [editing]);

  const selectedProduct = useMemo(() => products.find((p) => p._id === form.productId), [products, form.productId]);

  const calculatedM2 = useMemo(() => {
    const qty = Number(form.quantity || 0);
    if (!selectedProduct || !qty) return 0;
    const m2PerPiece = Number(selectedProduct.m2PerPiece || 0);
    const perPallet = Number(selectedProduct.perPallet || 0);
    if (form.unit === 'm2') return qty;
    if (form.unit === 'kom') return qty * m2PerPiece;
    // paleta
    return qty * perPallet * m2PerPiece;
  }, [form.quantity, form.unit, selectedProduct]);

  useEffect(() => {
    setForm((p) => ({ ...p, squareMeters: Number(calculatedM2.toFixed(2)) }));
  }, [calculatedM2]);

  const loadReferenceData = async () => {
    const [p, c] = await Promise.all([
      lagerFinishedProductsAPI.getAll(),
      lagerCompaniesAPI.getAll()
    ]);
    setProducts(Array.isArray(p) ? p : []);
    setCompanies(Array.isArray(c) ? c : []);
  };

  const loadTransports = async () => {
    const t = await lagerTransportAPI.getAll();
    setTransports(Array.isArray(t) ? t : []);
    setSelectedTransports([]);
  };

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadTransports(), loadReferenceData()]);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju transporta';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      productId: '',
      companyId: '',
      quantity: 0,
      unit: 'paleta',
      shippedDate: new Date(),
      squareMeters: 0,
      shippedTo: '',
      notes: ''
    });
    setError('');
    setDialogVisible(true);
  };

  const openEdit = (transport: LagerTransport) => {
    const productId =
      transport.originalProductId ||
      (typeof transport.productId === 'string' ? transport.productId : transport.productId?._id) ||
      '';
    const companyId = typeof transport.companyId === 'string' ? transport.companyId : transport.companyId?._id || '';
    setEditing(transport);
    setForm({
      productId,
      companyId,
      quantity: Number(transport.quantity || 0),
      unit: (transport.unit as LagerUnit) || 'paleta',
      shippedDate: toDate(transport.shippedDate),
      squareMeters: Number(transport.squareMeters || 0),
      shippedTo: transport.shippedTo || '',
      notes: transport.notes || ''
    });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!form.productId) return setError('Proizvod je obavezan');
    if (!form.companyId) return setError('Firma je obavezna');
    if (!form.quantity || form.quantity <= 0) return setError('Količina mora biti veća od 0');
    try {
      setError('');
      const payload = {
        productId: form.productId,
        companyId: form.companyId,
        quantity: form.quantity,
        unit: form.unit,
        squareMeters: form.squareMeters,
        shippedDate: form.shippedDate.toISOString(),
        shippedTo: form.shippedTo,
        notes: form.notes
      };
      if (editing?._id) {
        await lagerTransportAPI.update(editing._id, payload);
      } else {
        await lagerTransportAPI.create(payload);
      }
      setDialogVisible(false);
      await loadTransports();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju transporta';
      setError(msg);
      showCustomAlert.error(msg);
    }
  };

  const handleDelete = (transport: LagerTransport) => {
    showCustomAlert.confirm(
      'Da li ste sigurni da želite da obrišete transport?',
      async () => {
        try {
          await lagerTransportAPI.delete(transport._id);
          await loadTransports();
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju transporta';
          showCustomAlert.error(msg);
        }
      },
      'Potvrda brisanja'
    );
  };

  const handleBulkDelete = () => {
    const items = selectedTransports;
    if (!items.length) return;

    showCustomAlert.confirm(
      `Da li ste sigurni da želite da obrišete ${items.length} transport(a)?`,
      async () => {
        setBulkDeleting(true);
        try {
          const results = await Promise.allSettled(items.map((t) => lagerTransportAPI.delete(t._id)));
          const ok = results.filter((r) => r.status === 'fulfilled').length;
          const failed = results.length - ok;
          if (failed === 0) {
            showCustomAlert.success(`Obrisano: ${ok}`);
          } else {
            showCustomAlert.warning(`Obrisano: ${ok}, neuspešno: ${failed}`);
          }
          await loadTransports();
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju transporta';
          showCustomAlert.error(msg);
        } finally {
          setSelectedTransports([]);
          setBulkDeleting(false);
        }
      },
      'Potvrda brisanja'
    );
  };

  const handleSelectAll = () => setSelectedTransports(transports);
  const handleClearSelection = () => setSelectedTransports([]);

  const dateBody = (row: LagerTransport) => {
    const d = new Date(row.shippedDate);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('sr-RS');
  };

  const productBody = (row: LagerTransport) => {
    if (typeof row.productId !== 'string') return row.productId?.name || row.productName || '';
    return row.productName || '';
  };

  const companyBody = (row: LagerTransport) => {
    if (typeof row.companyId !== 'string') return row.companyId?.name || row.companyName || '';
    return row.companyName || '';
  };

  const unitBody = (row: LagerTransport) => (row.unit === 'm2' ? 'm²' : row.unit);

  const m2Body = (row: LagerTransport) => {
    const val = row.squareMeters ?? (row.unit === 'm2' ? row.quantity : 0);
    return typeof val === 'number' ? val.toFixed(2) : '';
  };

  const actionsBody = (row: LagerTransport) => (
    <div className="flex gap-2 justify-content-end">
      <Button icon="pi pi-pencil" className="action-icon-btn" text rounded onClick={() => openEdit(row)} tooltip="Izmeni" />
      <Button
        icon="pi pi-trash"
        className="action-icon-btn"
        text
        rounded
        severity="danger"
        onClick={() => handleDelete(row)}
        tooltip="Obriši"
      />
    </div>
  );

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Transport">
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="flex justify-content-end gap-2" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
          <Button
            icon="pi pi-check-square"
            label="Select all"
            severity="secondary"
            outlined
            onClick={handleSelectAll}
            disabled={!transports.length}
          />
          <Button
            icon="pi pi-times"
            label="Poništi"
            severity="secondary"
            outlined
            onClick={handleClearSelection}
            disabled={!selectedTransports.length}
          />
          <Button
            icon="pi pi-trash"
            label={`Obriši izabrane${selectedTransports.length ? ` (${selectedTransports.length})` : ''}`}
            severity="danger"
            outlined
            onClick={handleBulkDelete}
            disabled={!selectedTransports.length || bulkDeleting}
          />
          <Button icon="pi pi-plus" label="Dodaj" onClick={openCreate} />
          <Button icon="pi pi-refresh" label="Osveži" severity="secondary" outlined onClick={loadAll} />
        </div>

        <DataTable
          value={transports}
          loading={loading || bulkDeleting}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          responsiveLayout="stack" breakpoint="768px"
          dataKey="_id"
          selectionMode="checkbox"
          selection={selectedTransports}
          onSelectionChange={(e: DataTableSelectionMultipleChangeEvent<LagerTransport[]>) => setSelectedTransports(e.value || [])}
        >
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
          <Column header="Datum" body={dateBody} sortable sortField="shippedDate" />
          <Column header="Proizvod" body={productBody} sortable />
          <Column header="Firma" body={companyBody} sortable />
          <Column field="quantity" header="Količina" />
          <Column header="Jedinica" body={unitBody} />
          <Column header="Kvadrati (m²)" body={m2Body} />
          <Column header="Akcije" body={actionsBody} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={dialogHeader}
        visible={dialogVisible}
        style={{ width: '720px', maxWidth: '96vw' }}
        onHide={() => setDialogVisible(false)}
        modal
        closable
      >
        <div className="p-fluid">
          <div className="grid">
            <div className="col-12">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Proizvod</label>
              <Dropdown
                value={form.productId}
                options={products}
                optionLabel="name"
                optionValue="_id"
                placeholder="Izaberite proizvod"
                filter
                showClear
                appendTo="self"
                onChange={(e) => setForm((p) => ({ ...p, productId: e.value || '' }))}
              />
            </div>

            <div className="col-12">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Firma</label>
              <Dropdown
                value={form.companyId}
                options={companies}
                optionLabel="name"
                optionValue="_id"
                placeholder="Izaberite firmu"
                filter
                showClear
                appendTo="self"
                onChange={(e) => setForm((p) => ({ ...p, companyId: e.value || '' }))}
              />
            </div>

            <div className="col-12 md:col-6">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Količina</label>
              <InputNumber
                value={form.quantity}
                onValueChange={(e) => setForm((p) => ({ ...p, quantity: Number(e.value || 0) }))}
                min={0}
                useGrouping={false}
              />
            </div>

            <div className="col-12 md:col-6">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Kvadrati (m²)</label>
              <InputText value={`${form.squareMeters || 0} m²`} disabled />
              <div className="p-message p-message-info" style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem' }}>
                <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }} />
                Kalkulisano: {calculatedM2.toFixed(2)} m²
              </div>
            </div>

            <div className="col-12">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Jedinica</label>
              <Dropdown
                value={form.unit}
                options={unitOptions}
                optionLabel="label"
                optionValue="value"
                appendTo="self"
                onChange={(e) => setForm((p) => ({ ...p, unit: e.value }))}
              />
            </div>

            <div className="col-12">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Datum</label>
              <Calendar
                value={form.shippedDate}
                onChange={(e) => setForm((p) => ({ ...p, shippedDate: (e.value as Date) || new Date() }))}
                dateFormat="dd.mm.yy"
                appendTo="self"
                showIcon
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-content-end" style={{ marginTop: '1.25rem' }}>
            <Button label={editing ? 'Sačuvaj' : 'Sačuvaj'} icon="pi pi-check" onClick={handleSubmit} />
            <Button label="Otkaži" icon="pi pi-times" severity="secondary" outlined onClick={() => setDialogVisible(false)} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LagerTransportPage;


